import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const getMethodKey = (method) =>
  `${method?.payment_id}-${method?.integration?.id || method?.integration_id || 0}`;

const getPaymentVisual = (method) => {
  const paymentName = (method?.payment || "").toLowerCase();
  const integrationName = (method?.integration?.name || "").toLowerCase();

  if (method?.payment_id === "POS_PHYSICAL" || paymentName.includes("pagar con pos")) {
    return { icon: "🧑‍🍳", title: method.payment, sub: "Cobro en el POS del mesero" };
  }

  if (paymentName.includes("yappy")) {
    return { icon: "📱", title: method.payment, sub: "Pago instantáneo" };
  }

  if (
    paymentName.includes("evertech") ||
    paymentName.includes("powertranz") ||
    paymentName.includes("tilopay") ||
    integrationName.includes("pasarela")
  ) {
    return { icon: "💳", title: method.payment, sub: "Tarjeta de crédito/débito" };
  }

  if (
    paymentName.includes("transf./deposito a cta. bancaria") ||
    paymentName.includes("deposito") ||
    paymentName.includes("bancaria")
  ) {
    return { icon: "🏦", title: method.payment, sub: "Transferencia bancaria" };
  }

  if (
    paymentName.includes("efectivo") ||
    paymentName.includes("cash") ||
    paymentName.includes("contra entrega")
  ) {
    return { icon: "💵", title: method.payment, sub: "Pago al recibir" };
  }

  if (paymentName.includes("credito")) {
    return { icon: "🧾", title: method.payment, sub: "Crédito" };
  }

  return {
    icon: "💰",
    title: method.payment || "Método de pago",
    sub: method?.integration?.name || "Disponible"
  };
};

export default function PaymentStep({ methods = [], selected, onSelect }) {
  const trackRef = useRef(null);
  const selectedRef = useRef(null);
  const [arrows, setArrows] = useState({ left: false, right: false });

  /* Una sola tarjeta activa: primero coincidencia exacta por key y solo
     si no existe se cae al payment_id, así los métodos que comparten id
     y solo cambian de integration ya no se marcan en grupo. */
  const activeKey = useMemo(() => {
    const value = String(selected ?? "");
    if (!value) return null;

    const exact = methods.find((m) => getMethodKey(m) === value);
    if (exact) return getMethodKey(exact);

    const byPaymentId = methods.find((m) => String(m.payment_id) === value);
    return byPaymentId ? getMethodKey(byPaymentId) : null;
  }, [methods, selected]);

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setArrows({ left: el.scrollLeft > 4, right: el.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });

    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [updateArrows, methods.length]);

  // Deja visible la tarjeta ya seleccionada al montar
  useEffect(() => {
    const el = trackRef.current;
    const card = selectedRef.current;
    if (!el || !card) return;
    el.scrollLeft = card.offsetLeft - (el.clientWidth - card.offsetWidth) / 2;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector(".ch-v2-card");
    const step = card ? (card.offsetWidth + 12) * 2 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (!methods.length) {
    return <div className="ch-v2-empty">No hay métodos de pago disponibles.</div>;
  }

  return (
    <div className="ch-v2-pay">
      <button
        type="button"
        className="ch-v2-nav ch-v2-nav-prev"
        onClick={() => scroll(-1)}
        disabled={!arrows.left}
        aria-label="Ver métodos anteriores"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <div className="ch-v2-track" ref={trackRef} role="radiogroup" aria-label="Método de pago">
        {methods.map((method) => {
          const methodKey = getMethodKey(method);
          const isSelected = methodKey === activeKey;
          const visual = getPaymentVisual(method);

          return (
            <label
              key={methodKey}
              ref={isSelected ? selectedRef : null}
              className={`ch-v2-card${isSelected ? " ch-v2-selected" : ""}`}
              onClick={() => onSelect(method)}
            >
              <input
                type="radio"
                className="ch-v2-input"
                name="ch-v2-payment-method"
                readOnly
                checked={isSelected}
              />

              <span className="ch-v2-check" aria-hidden="true">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>

              <span className="ch-v2-icon">{visual.icon}</span>

              <span className="ch-v2-body">
                <span className="ch-v2-title">{visual.title}</span>
                <span className="ch-v2-sub">{visual.sub}</span>
              </span>
            </label>
          );
        })}
      </div>

      <button
        type="button"
        className="ch-v2-nav ch-v2-nav-next"
        onClick={() => scroll(1)}
        disabled={!arrows.right}
        aria-label="Ver más métodos"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </div>
  );
}